// WAAP Quick Setup — the onboarding wizard, backed by the PRIVATE portal
// /quick_setup via eagle_waf/quicksetup. Service-scoped: @mgmt resolves the vendor
// customer (service_zones.uid) from the SERVICE, so onboarding works before the
// account has any domain (no carrier-domain anchor needed).
//
//   GET  /services/:serviceId/waap/quick-setup                   → list sessions
//   POST /services/:serviceId/waap/quick-setup                   → create
//   PUT  /services/:serviceId/waap/quick-setup                   → update step
//   POST /services/:serviceId/waap/quick-setup/:setupId/execute  → provision
//
// The wizard creates one session (POST → id + default config), round-trips the
// whole object per step (PUT), then executes to provision the website.

import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** The CDN (Domain & Origin) slice of the quick-setup config. */
export interface QuickSetupCdn {
  /** Website domain (the public address visitors use). */
  name: string;
  /** Origin host (no protocol). */
  urlPrefix: string;
  /** Origin protocol, e.g. "https://" | "http://". */
  protocol: string;
  /** Origin servers — each a full URL ({urlPrefix: "https://origin"}). */
  origins: { urlPrefix: string }[];
  streamingService: boolean;
  redirectHttpToHttps: boolean;
  http2: boolean;
  active: boolean;
}

/** The quick-setup config (owned by the FE, round-tripped each step). */
export interface QuickSetupConfig {
  cdn: QuickSetupCdn;
  /** type: "auto" (auto-generated) | "existing" (use existing certificate). For
   *  "existing", certificateId is the vendor pool cert id (numeric on the wire —
   *  matches the flashcone console payload). */
  certificate: { type: string; certificateId: string | number };
  /** AI-WAF protection — policy is the WAF policy id (1 = Basic). */
  aiWaf: { enabled: boolean; policy: number };
  apiProtection: { enabled: boolean };
  botManagement: { enabled: boolean };
}

/** The quick-setup session record (POST/PUT response). */
export interface QuickSetupRecord {
  id: number;
  config: QuickSetupConfig;
  solution_id: number;
  current_step: number;
  configured_step: number;
  total_step_num: number;
  executed_step: number;
  domain_id: number;
  cdn_domain_id: number;
  status: number;
  customer_id: number;
}

/** The execute response (provisioning result). `isSuccess:false` means a step
 *  failed (e.g. certificate config) — `message`/`detailMsg`/`failedStep` describe it. */
export interface QuickSetupExecuteResult {
  deliveryDomain: string;
  isSuccess: boolean;
  failedStep?: number;
  message?: string;
  detailMsg?: string;
}

/** Body for create (step 1) / update (steps 2-4). */
export interface QuickSetupWrite {
  id?: number;
  solution_id?: number;
  current_step: number;
  configured_step: number;
  config: QuickSetupConfig;
}

function qsPath(serviceId: string): string {
  return `/services/${serviceId}/waap/quick-setup`;
}

/** List the account's quick-setup sessions (the Resume step's source), resolved
 *  from the WAAP service (works before the account has any domain). */
export function useQuickSetupSessions(serviceId: string | undefined) {
  return useQuery({
    queryKey: ['waap', 'quick-setup', serviceId ?? ''],
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<QuickSetupRecord[]> => {
      const res = await serviceApiV3.get<QuickSetupRecord[] | { data: QuickSetupRecord[] }>(
        qsPath(serviceId as string),
      );
      const body = res.data as QuickSetupRecord[] | { data?: QuickSetupRecord[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Create a new quick-setup session (POST). Returns the created record. */
export function useCreateQuickSetup(
  serviceId: string,
  options?: UseMutationOptions<QuickSetupRecord, unknown, QuickSetupWrite>,
) {
  return useMutation({
    mutationFn: async (body: QuickSetupWrite): Promise<QuickSetupRecord> => {
      const res = await serviceApiV3.post<QuickSetupRecord>(qsPath(serviceId), body);
      return res.data;
    },
    ...options,
  });
}

/** Update the quick-setup session for the current step (PUT). */
export function useUpdateQuickSetup(
  serviceId: string,
  options?: UseMutationOptions<QuickSetupRecord, unknown, QuickSetupWrite>,
) {
  return useMutation({
    mutationFn: async (body: QuickSetupWrite): Promise<QuickSetupRecord> => {
      const res = await serviceApiV3.put<QuickSetupRecord>(qsPath(serviceId), body);
      return res.data;
    },
    ...options,
  });
}

/**
 * Upload a Tiger inventory cert into the vendor's managed-cert pool (service-scoped,
 * no domain needed during onboarding) and return the vendor pool cert id. The wizard
 * then references that id as `certificate.type = 'existing'` so provisioning binds the
 * Tiger cert in one pass. POST /services/:serviceId/waap/ssl/certinvent-upload.
 */
export function useCertinventUpload(
  serviceId: string,
  options?: UseMutationOptions<number, unknown, { certId: string }>,
) {
  return useMutation({
    mutationFn: async ({ certId }: { certId: string }): Promise<number> => {
      const res = await serviceApiV3.post<{ certificate_id: number }>(
        `/services/${serviceId}/waap/ssl/certinvent-upload`,
        { cert_id: certId },
      );
      return res.data.certificate_id;
    },
    ...options,
  });
}

/** Execute (provision) a finished quick-setup session (POST). The `domain` is
 *  passed so the backend can register the provisioned website into the registry
 *  (so it appears in the WAAP websites list). */
export function useExecuteQuickSetup(
  serviceId: string,
  options?: UseMutationOptions<QuickSetupExecuteResult, unknown, { setupId: number; domain: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ setupId, domain }: { setupId: number; domain: string }): Promise<QuickSetupExecuteResult> => {
      const res = await serviceApiV3.post<QuickSetupExecuteResult>(
        `${qsPath(serviceId)}/${setupId}/execute`,
        { domain },
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      // The backend registers the provisioned website into the WAAP domain
      // registry; invalidate the websites list so it appears without a reload.
      void qc.invalidateQueries({ queryKey: ['waap', 'service', serviceId, 'domains'] });
      void qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      options?.onSuccess?.(...args);
    },
  });
}
